// -- Lifecycle hooks
if (!customElements.get('momax-featured-picks')) {
  customElements.define('momax-featured-picks', class extends HTMLElement {
    connectedCallback() {
      // -- State and variables
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.momax-featured-picks__track');
      this.cards = [...this.querySelectorAll('[data-fp-card]')];
      this.progress = this.querySelector('.momax-featured-picks__progress');
      this.mobile = matchMedia('(max-width: 740px)');
      const initialFilter = this.querySelector('[data-fp-filter][aria-pressed="true"]')?.dataset.fpFilter || '1';
      this.cards.forEach(card => { card.hidden = card.dataset.category !== initialFilter; });

      // -- Listeners
      this.addEventListener('click', event => {
        const filter = event.target.closest('[data-fp-filter]');
        const direction = event.target.closest('[data-fp-direction]');
        if (filter) this.filter(filter.dataset.fpFilter);
        if (direction) {
          const card = this.visibleCards()[0];
          if (!card) return;
          const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap);
          this.track.scrollBy({ left: step * Number(direction.dataset.fpDirection), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }, { signal });
      this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
      this.mobile.addEventListener('change', () => this.rebuild(), { signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-fp-card]');
        if (card) {
          if (card.dataset.category) this.filter(card.dataset.category);
          this.track.scrollLeft += card.getBoundingClientRect().left - this.track.getBoundingClientRect().left - parseFloat(getComputedStyle(this.track).paddingLeft);
        }
      }, { signal });

      // -- Desktop drag-to-scroll
      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let hasDragged = false;
      let lastX = 0;
      let lastTime = 0;
      let velocityX = 0;

      const onMouseDown = event => {
        if (event.button !== 0 || this.mobile.matches) return;
        isDown = true;
        hasDragged = false;
        startX = event.pageX;
        lastX = event.pageX;
        lastTime = performance.now();
        velocityX = 0;
        startScrollLeft = this.track.scrollLeft;
      };

      const onMouseMove = event => {
        if (!isDown) return;
        const now = performance.now();
        const dt = now - lastTime;
        if (dt > 0) {
          velocityX = (event.pageX - lastX) / dt;
          lastX = event.pageX;
          lastTime = now;
        }
        const deltaX = event.pageX - startX;
        if (!hasDragged && Math.abs(deltaX) > 4) {
          hasDragged = true;
          this.track.classList.add('is-dragging');
          this.track.style.scrollSnapType = 'none';
          this.track.style.scrollBehavior = 'auto';
        }
        if (hasDragged) {
          event.preventDefault();
          this.track.scrollLeft = startScrollLeft - deltaX;
        }
      };

      const onMouseUp = () => {
        if (!isDown) return;
        isDown = false;
        if (hasDragged) {
          this.track.style.scrollSnapType = '';
          this.track.style.scrollBehavior = '';

          const cards = this.visibleCards();
          const origin = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).paddingLeft);
          let closestIndex = 0;
          let minDistance = Infinity;
          cards.forEach((card, index) => {
            const dist = card.getBoundingClientRect().left - origin;
            if (Math.abs(dist) < minDistance) {
              minDistance = Math.abs(dist);
              closestIndex = index;
            }
          });

          if (velocityX < -0.25 && closestIndex < cards.length - 1) {
            closestIndex = Math.min(cards.length - 1, closestIndex + 1);
          } else if (velocityX > 0.25 && closestIndex > 0) {
            closestIndex = Math.max(0, closestIndex - 1);
          }

          const targetCard = cards[closestIndex];
          if (targetCard) {
            const targetLeft = this.track.scrollLeft + targetCard.getBoundingClientRect().left - origin;
            const max = this.track.scrollWidth - this.track.clientWidth;
            this.track.scrollTo({
              left: Math.max(0, Math.min(max, targetLeft)),
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
            });
          }

          setTimeout(() => {
            hasDragged = false;
            this.track.classList.remove('is-dragging');
          }, 50);
        }
      };

      this.track.addEventListener('mousedown', onMouseDown, { signal });
      window.addEventListener('mousemove', onMouseMove, { signal });
      window.addEventListener('mouseup', onMouseUp, { signal });
      window.addEventListener('blur', onMouseUp, { signal });
      this.track.addEventListener('dragstart', event => {
        if (!this.mobile.matches) event.preventDefault();
      }, { signal });
      this.track.addEventListener('click', event => {
        if (hasDragged) {
          event.preventDefault();
          event.stopPropagation();
          hasDragged = false;
          this.track.classList.remove('is-dragging');
        }
      }, { capture: true, signal });

      this.shadowCarousel = this.querySelector('.momax-featured-picks__carousel');
      this.track.addEventListener('pointerover', () => this.updateHoverShadow(), { signal: this.controller.signal });
      this.track.addEventListener('pointerout', () => this.clearHoverShadow(), { signal: this.controller.signal });
      this.track.addEventListener('pointerleave', () => this.clearHoverShadow(), { signal: this.controller.signal });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.rebuild();
    }

    // -- Functions
    visibleCards() {
      const cards = this.cards.filter(card => !card.hidden);
      return this.mobile.matches ? cards.sort((a, b) => Number(a.style.getPropertyValue('--fp-mobile-order')) - Number(b.style.getPropertyValue('--fp-mobile-order'))) : cards;
    }

    filter(category) {
      const changed = this.querySelector('[data-fp-filter][aria-pressed="true"]')?.dataset.fpFilter !== category;
      this.querySelectorAll('[data-fp-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fpFilter === category)));
      this.cards.forEach(card => { card.hidden = card.dataset.category !== category; });
      this.track.scrollLeft = 0;
      this.querySelector('.momax-featured-picks__empty').hidden = this.visibleCards().length > 0;
      this.rebuild();
      if (changed) this.dispatchEvent(new CustomEvent('momax:featured-picks:filter-change', { bubbles: true }));
    }

    rebuild() {
      const cards = this.visibleCards();
      this.querySelector('.momax-featured-picks__empty').hidden = cards.length > 0;
      this.progress.replaceChildren(...cards.map(() => document.createElement('span')));
      this.update();
    }

    clearHoverShadow() {
      this.shadowCarousel?.classList.remove('has-hover-shadow');
      this.querySelector('.has-external-shadow')?.classList.remove('has-external-shadow');
    }

    updateHoverShadow() {
      this.clearHoverShadow();
      if (!this.shadowCarousel || !matchMedia('(min-width: 741px) and (hover: hover) and (pointer: fine)').matches) return;
      const card = this.querySelector('.momax-featured-picks__card:hover');
      if (!card || !card.getClientRects().length) return;
      const bounds = card.getBoundingClientRect();
      const viewport = this.track.getBoundingClientRect();
      // Partially visible cards keep their normal clipped shadow, never a phantom outer edge.
      if (bounds.left < viewport.left - 0.5 || bounds.right > viewport.right + 0.5) return;
      const carousel = this.shadowCarousel.getBoundingClientRect();
      for (const [property, value] of Object.entries({left: bounds.left - carousel.left, top: bounds.top - carousel.top, width: bounds.width, height: bounds.height})) {
        this.shadowCarousel.style.setProperty('--hover-' + property, value + 'px');
      }
      card.classList.add('has-external-shadow');
      this.shadowCarousel.classList.add('has-hover-shadow');
    }

    update() {
      this.updateHoverShadow();
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelectorAll('[data-fp-direction]').forEach(button => {
        button.disabled = max <= 1 || (Number(button.dataset.fpDirection) < 0 ? this.track.scrollLeft <= 1 : this.track.scrollLeft >= max - 1);
      });
      const cards = this.visibleCards();
      const origin = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).paddingLeft);
      let active = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const offset = Math.abs(card.getBoundingClientRect().left - origin);
        if (offset < distance) { active = index; distance = offset; }
      });
      if (max > 1 && this.track.scrollLeft >= max - 1) active = cards.length - 1;
      [...this.progress.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
      this.progress.hidden = cards.length < 2;
    }

    // -- Lifecycle hooks
    disconnectedCallback() {
      this.clearHoverShadow();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }
  });
}
